export interface Session {
  id: string;
  name: string;
  projectId: string;
  createdAt: Date;
  updatedAt: Date;
  lastMessage?: string;
  messageCount: number;
  isActive: boolean;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  subagent?: string;
  slashCommand?: string;
}

export interface Subagent {
  id: string;
  name: string;
  description: string;
  tools: string[];
  systemPrompt: string;
  isActive: boolean;
}

import { ClaudeCodeSDKService, ClaudeCodeConfig } from './claudeCodeSDK';

export class SessionManager {
  private projectId: string;
  private currentSessionId: string | null = null;
  private sdkService: ClaudeCodeSDKService;

  constructor(projectId: string, projectPath: string) {
    this.projectId = projectId;
    
    // Initialize Byte DevOps SDK
    const config: ClaudeCodeConfig = {
      projectPath,
      model: 'claude-3-5-sonnet-20241022',
      maxTurns: 10,
      allowedTools: ['Read', 'Edit', 'Write', 'Glob', 'Grep', 'Bash', 'Task'],
      permissionMode: 'default'
    };
    
    this.sdkService = new ClaudeCodeSDKService(config);
  }

  // Session Management
  async createSession(name: string): Promise<Session> {
    const session: Session = {
      id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name,
      projectId: this.projectId,
      createdAt: new Date(),
      updatedAt: new Date(),
      messageCount: 0,
      isActive: true
    };

    // Save to localStorage
    const sessions = this.getSessions();
    sessions.push(session);
    localStorage.setItem(`sessions_${this.projectId}`, JSON.stringify(sessions));

    this.currentSessionId = session.id;
    return session;
  }

  async getSessions(): Promise<Session[]> {
    const sessionsJson = localStorage.getItem(`sessions_${this.projectId}`);
    if (!sessionsJson) return [];

    try {
      const sessions = JSON.parse(sessionsJson);
      return sessions.map((session: any) => ({
        ...session,
        createdAt: new Date(session.createdAt),
        updatedAt: new Date(session.updatedAt)
      }));
    } catch (error) {
      console.error('Failed to parse sessions:', error);
      return [];
    }
  }

  async getCurrentSession(): Promise<Session | null> {
    if (!this.currentSessionId) return null;
    
    const sessions = await this.getSessions();
    return sessions.find(s => s.id === this.currentSessionId) || null;
  }

  async setCurrentSession(sessionId: string): Promise<void> {
    this.currentSessionId = sessionId;
    
    // Update session as active
    const sessions = await this.getSessions();
    const updatedSessions = sessions.map(session => ({
      ...session,
      isActive: session.id === sessionId
    }));
    localStorage.setItem(`sessions_${this.projectId}`, JSON.stringify(updatedSessions));
  }

  async deleteSession(sessionId: string): Promise<void> {
    const sessions = await this.getSessions();
    const filteredSessions = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(`sessions_${this.projectId}`, JSON.stringify(filteredSessions));

    // Delete associated messages
    localStorage.removeItem(`messages_${sessionId}`);

    if (this.currentSessionId === sessionId) {
      this.currentSessionId = null;
    }
  }

  // Message Management
  async addMessage(message: Omit<ChatMessage, 'id' | 'timestamp'>): Promise<ChatMessage> {
    const chatMessage: ChatMessage = {
      ...message,
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };

    // Save message
    const messages = await this.getMessages(message.sessionId);
    messages.push(chatMessage);
    localStorage.setItem(`messages_${message.sessionId}`, JSON.stringify(messages));

    // Update session
    const sessions = await this.getSessions();
    const updatedSessions = sessions.map(session => {
      if (session.id === message.sessionId) {
        return {
          ...session,
          updatedAt: new Date(),
          lastMessage: message.content.substring(0, 100),
          messageCount: session.messageCount + 1
        };
      }
      return session;
    });
    localStorage.setItem(`sessions_${this.projectId}`, JSON.stringify(updatedSessions));

    return chatMessage;
  }

  async getMessages(sessionId: string): Promise<ChatMessage[]> {
    const messagesJson = localStorage.getItem(`messages_${sessionId}`);
    if (!messagesJson) return [];

    try {
      const messages = JSON.parse(messagesJson);
      return messages.map((message: any) => ({
        ...message,
        timestamp: new Date(message.timestamp)
      }));
    } catch (error) {
      console.error('Failed to parse messages:', error);
      return [];
    }
  }

  async getCurrentSessionMessages(): Promise<ChatMessage[]> {
    if (!this.currentSessionId) return [];
    return this.getMessages(this.currentSessionId);
  }

  // Subagent Management
  async getSubagents(): Promise<Subagent[]> {
    try {
      const response = await fetch(`/api/projects/${this.projectId}/subagents`);
      if (!response.ok) return [];

      const subagents = await response.json();
      return subagents.map((agent: any) => ({
        id: agent.id,
        name: agent.name,
        description: agent.description,
        tools: agent.tools || [],
        systemPrompt: agent.systemPrompt || '',
        isActive: agent.isActive !== false
      }));
    } catch (error) {
      console.error('Failed to fetch subagents:', error);
      return [];
    }
  }

  // Byte DevOps SDK Integration
  async sendMessage(content: string, subagent?: string): Promise<ChatMessage[]> {
    if (!this.currentSessionId) {
      throw new Error('No active session');
    }

    // Add user message
    const userMessage = await this.addMessage({
      sessionId: this.currentSessionId,
      type: 'user',
      content,
      subagent
    });

    // Check for slash commands
    const slashCommand = this.detectSlashCommand(content);
    if (slashCommand) {
      const systemMessage = await this.addMessage({
        sessionId: this.currentSessionId,
        type: 'system',
        content: `Executing slash command: ${slashCommand}`,
        slashCommand
      });
    }

    try {
      // Use real Byte DevOps SDK
      const sdkResponse = await this.sdkService.sendMessage(
        content,
        this.currentSessionId,
        subagent
      );

      // Update session ID if it changed
      if (sdkResponse.sessionId !== this.currentSessionId) {
        this.currentSessionId = sdkResponse.sessionId;
      }

      // Convert SDK messages to our format and save them
      const formattedMessages: ChatMessage[] = [];
      
      for (const sdkMessage of sdkResponse.messages) {
        const formatted = ClaudeCodeSDKService.formatSDKMessage(sdkMessage);
        
        // Only save assistant and system messages (user message already saved above)
        if (formatted.type === 'assistant' || formatted.type === 'system') {
          const chatMessage = await this.addMessage({
            sessionId: this.currentSessionId,
            type: formatted.type,
            content: formatted.content,
            subagent: formatted.subagent,
            slashCommand: formatted.slashCommand
          });
          formattedMessages.push(chatMessage);
        }
      }

      return [userMessage, ...formattedMessages];
    } catch (error) {
      console.error('Failed to get Claude response:', error);
      
      // Fallback to mock response
      const assistantMessage = await this.addMessage({
        sessionId: this.currentSessionId,
        type: 'assistant',
        content: this.generateMockResponse(content, subagent),
        subagent
      });

      return [userMessage, assistantMessage];
    }
  }

  private detectSlashCommand(content: string): string | undefined {
    const slashMatch = content.match(/^\/(\w+)/);
    return slashMatch ? slashMatch[1] : undefined;
  }

  private generateMockResponse(content: string, subagent?: string): string {
    if (subagent) {
      return `I'm responding as the ${subagent} subagent. Based on your request: "${content}", I'll help you with specialized assistance in my area of expertise.`;
    }

    if (content.startsWith('/')) {
      return `Slash command executed successfully. How can I help you further?`;
    }

    return `I understand you want to "${content}". Let me help you with that. You can use the terminal below to run commands, or I can guide you through the process.`;
  }

  // Session resumption (for Byte DevOps SDK integration)
  async resumeSession(sessionId: string): Promise<Session | null> {
    const sessions = await this.getSessions();
    const session = sessions.find(s => s.id === sessionId);
    
    if (session) {
      await this.setCurrentSession(sessionId);
      return session;
    }
    
    return null;
  }

  // Export session data
  async exportSession(sessionId: string): Promise<{ session: Session; messages: ChatMessage[] }> {
    const sessions = await this.getSessions();
    const session = sessions.find(s => s.id === sessionId);
    const messages = await this.getMessages(sessionId);

    if (!session) {
      throw new Error('Session not found');
    }

    return { session, messages };
  }

  // Import session data
  async importSession(sessionData: { session: Session; messages: ChatMessage[] }): Promise<void> {
    const sessions = await this.getSessions();
    sessions.push(sessionData.session);
    localStorage.setItem(`sessions_${this.projectId}`, JSON.stringify(sessions));
    localStorage.setItem(`messages_${sessionData.session.id}`, JSON.stringify(sessionData.messages));
  }
}
